import { pascalCase } from 'change-case'
import { axiosGet } from '../../util/api'

/**
 * The uuid the poller keys on. It lives under the progress class entry, but the
 * dashboard has always accepted the first entry as a fallback.
 *
 * Shared so a component that starts its own run — the campaigns table's bid
 * adjustments editor, for example — registers the same uuid the toolbar's poller
 * will report as finished.
 */
export const extractProgressUuid = (progress: unknown, name: string): string | undefined => {
    if (!progress || typeof progress !== 'object') {
        return undefined
    }

    const entries = Object.values(progress as Record<string, any>)
    const namedEntry = (progress as Record<string, any>)[pascalCase(name)]
    return (
        namedEntry?.uuid ?? namedEntry?.extra?.uuid ?? entries[0]?.uuid ?? entries[0]?.extra?.uuid
    )
}

export type BulkActionRunInfo = {
    counts?: {
        success?: number
        failed?: number
    }
    message?: string
}

export type BulkActionTerminalOutcome = {
    response?: any
    error?: any
    presented?: boolean
    refreshed?: boolean
}
type CompletionHandler = (uuid: string, outcome?: BulkActionTerminalOutcome) => void

const completionHandlers = new Map<string, CompletionHandler>()
const pendingAcceptance = new Map<string, boolean>()
const unownedCompletions = new Set<string>()
const unownedCompletionOrder: string[] = []
const deliveredCompletions = new Set<string>()
const deliveredOutcomes = new Map<string, BulkActionTerminalOutcome>()
const deliveredCompletionOrder: string[] = []
const MAX_REMEMBERED_COMPLETIONS = 500

export type BulkActionCompletionDelivery = 'owned' | 'unowned' | 'duplicate'

const rememberDeliveredCompletion = (uuid: string, outcome?: BulkActionTerminalOutcome) => {
    deliveredCompletions.add(uuid)
    if (outcome) {
        deliveredOutcomes.set(uuid, outcome)
    }
    deliveredCompletionOrder.push(uuid)

    if (deliveredCompletionOrder.length > MAX_REMEMBERED_COMPLETIONS) {
        const oldestUuid = deliveredCompletionOrder.shift()
        if (oldestUuid) {
            deliveredCompletions.delete(oldestUuid)
            deliveredOutcomes.delete(oldestUuid)
        }
    }
}

const rememberUnownedCompletion = (uuid: string) => {
    unownedCompletions.add(uuid)
    unownedCompletionOrder.push(uuid)

    if (unownedCompletionOrder.length > MAX_REMEMBERED_COMPLETIONS) {
        const oldestUuid = unownedCompletionOrder.shift()
        if (oldestUuid) {
            unownedCompletions.delete(oldestUuid)
        }
    }
}

/**
 * Associates a server-side run with the BulkActions instance that started it.
 * The registry is module-scoped because any mounted poller may be the one that
 * observes completion; only the initiating instance owns the table callback.
 */
export const registerBulkActionRun = (
    uuid: string,
    handler: CompletionHandler,
    awaitingAcceptance = false,
) => {
    completionHandlers.set(uuid, handler)
    if (awaitingAcceptance) {
        pendingAcceptance.set(uuid, false)
    }
    const deliveredOutcome = deliveredOutcomes.get(uuid)
    const completedBeforeRegistration =
        unownedCompletions.delete(uuid) || deliveredOutcome !== undefined
    if (completedBeforeRegistration) {
        Promise.resolve().then(() => {
            if (completionHandlers.get(uuid) !== handler) {
                return
            }
            completionHandlers.delete(uuid)
            pendingAcceptance.delete(uuid)
            if (!deliveredCompletions.has(uuid)) {
                rememberDeliveredCompletion(uuid)
            }
            if (deliveredOutcome === undefined) {
                handler(uuid)
            } else {
                handler(uuid, deliveredOutcome)
            }
        })
    }

    return () => {
        if (completionHandlers.get(uuid) === handler) {
            completionHandlers.delete(uuid)
            pendingAcceptance.delete(uuid)
        }
    }
}

/** Resolves a run with no current owner, while still allowing a late owner to clean up. */
export const resolveUnownedBulkActionCompletion = (
    uuid: string,
    fallback: (outcome: BulkActionTerminalOutcome) => {
        presented: boolean
        refreshed: boolean
    },
) => {
    axiosGet('bulk-actions/run-info', { uuid, nonblocking: true }, false, { ignore: true })
        .then((response) => ({ response }))
        .catch((error) => ({ error }))
        .then((outcome: BulkActionTerminalOutcome) => {
            if (deliveredCompletions.has(uuid)) {
                return
            }
            const handler = completionHandlers.get(uuid)
            if (handler) {
                notifyBulkActionRunCompleted(uuid, outcome)
                return
            }
            if (!unownedCompletions.delete(uuid)) {
                return
            }
            const handling = fallback(outcome)
            rememberDeliveredCompletion(uuid, { ...outcome, ...handling })
        })
}

/** Stops terminal probing once the executor-owned progress entry is visible. */
export const observeBulkActionProgress = (progress: Record<string, any>) => {
    const activeUuids = new Set(
        Object.values(progress ?? {})
            .map((entry) => entry?.uuid ?? entry?.extra?.uuid)
            .filter(Boolean),
    )
    activeUuids.forEach((uuid) => pendingAcceptance.delete(uuid))
}

/**
 * Polls terminal keys for accepted HTTP requests that have not appeared in the
 * active-progress hash yet. A 404 is the expected queue-hop state.
 */
export const pollPendingBulkActionRuns = () => {
    pendingAcceptance.forEach((inFlight, uuid) => {
        if (inFlight) {
            return
        }

        pendingAcceptance.set(uuid, true)
        axiosGet('bulk-actions/run-info', { uuid, nonblocking: true }, false, { ignore: true })
            .then((response) => notifyBulkActionRunCompleted(uuid, { response }))
            .catch((error) => {
                if (error?.response && error.response.status !== 404) {
                    notifyBulkActionRunCompleted(uuid, { error })
                }
            })
            .finally(() => {
                if (pendingAcceptance.has(uuid)) {
                    pendingAcceptance.set(uuid, false)
                }
            })
    })
}

/** Delivers a disappeared progress entry once, regardless of which poller saw it. */
export const notifyBulkActionRunCompleted = (
    uuid: string,
    outcome?: BulkActionTerminalOutcome,
): BulkActionCompletionDelivery => {
    if (deliveredCompletions.has(uuid)) {
        return 'duplicate'
    }

    const handler = completionHandlers.get(uuid)
    if (!handler) {
        if (unownedCompletions.has(uuid)) {
            return 'duplicate'
        }
        rememberUnownedCompletion(uuid)
        return 'unowned'
    }

    rememberDeliveredCompletion(uuid)
    completionHandlers.delete(uuid)
    pendingAcceptance.delete(uuid)
    if (outcome === undefined) {
        handler(uuid)
    } else {
        handler(uuid, outcome)
    }
    return 'owned'
}

/** A resolved run-info response mutated data unless it explicitly reports zero successes. */
export const terminalResultHasMutation = (result: BulkActionRunInfo) => {
    if (typeof result.counts?.success === 'number') {
        return result.counts.success > 0
    }

    return true
}

// Test isolation for the module-scoped coordinator.
export const resetBulkActionExecutionForTests = () => {
    completionHandlers.clear()
    pendingAcceptance.clear()
    unownedCompletions.clear()
    unownedCompletionOrder.length = 0
    deliveredCompletions.clear()
    deliveredOutcomes.clear()
    deliveredCompletionOrder.length = 0
}
